// Terms of Service (/terms) and Privacy Policy (/privacy). Plain pages that
// work without logging in; the login window links to them.
// TODO(creator): add a contact email and have both texts checked by a lawyer.
import type { ReactNode } from "react";
import { RoomBackdrop } from "../common/RoomBackdrop";

export type LegalKind = "terms" | "privacy";

export const LEGAL_UPDATED = "October 17, 2026";

/** "/terms" -> "terms", "/privacy/" -> "privacy", anything else -> null. */
export function legalPageFor(pathname: string): LegalKind | null {
  const p = pathname.replace(/\/+$/, "").toLowerCase();
  if (p === "/terms") return "terms";
  if (p === "/privacy") return "privacy";
  return null;
}

interface Section {
  title: string;
  body: ReactNode;
}

const TERMS: Section[] = [
  {
    title: "1. Agreeing to these terms",
    body: (
      <p>
        Wizard Poker ("the game") is a free online card game at wizardpoker.online. By making an account or playing, you agree to these
        terms. If you do not agree, please do not use the game.
      </p>
    ),
  },
  {
    title: "2. Your account",
    body: (
      <ul>
        <li>
          <strong>Players are expected to be 13 or older to play.</strong> If you are younger, please do not make an account.
        </li>
        <li>Keep your password secret. You are responsible for what happens on your account.</li>
        <li>Your display name must not be hateful, sexual, threatening or pretend to be someone else.</li>
        <li>You can delete your account at any time in Settings → Account.</li>
      </ul>
    ),
  },
  {
    title: "3. Coins and items have no real-world value",
    body: (
      <ul>
        <li>Coins, orbs and cosmetic items are game points only. They are not money and cannot be sold, traded for money or cashed out.</li>
        <li>We may change prices, rewards and balances to keep the game fair and fun.</li>
        <li>If coins or items are gained through a bug or cheating, we may remove them.</li>
        <li>
          The slot machine uses these play coins only. You cannot buy coins, and coins you win cannot be cashed out. Over time the
          machine pays back a little less than it takes, like a real one.
        </li>
        <li>
          Shop items and orbs are bought with play coins only. An orb gives a random cosmetic item; the odds are shown in the shop
          before you open one. If you already own the item you get 1/3 of its shop price back in coins. Purchases cannot be undone.
        </li>
        <li>The drinks, cigars and slot machine in the room are cartoon props for atmosphere.</li>
      </ul>
    ),
  },
  {
    title: "4. Chat and reactions",
    body: (
      <ul>
        <li>Everyone at your table can read what you write in the table chat and see your reactions.</li>
        <li>Be kind: no hate, threats, sexual content, harassment, scams, or sharing anyone's personal details (including your own).</li>
        <li>
          Chat has a budget: every character costs one token; you have 500 and get 25 back each second. Spamming past it times you out
          until the budget is full again.
        </li>
        <li>Admins may remove chat access or accounts that break these rules.</li>
      </ul>
    ),
  },
  {
    title: "5. Fair play",
    body: (
      <>
        <p>Assembling spells, bluffing and reporting are part of the game. Breaking the game is not. Do not:</p>
        <ul>
          <li>use bots, scripts, modified clients or bugs to get an advantage;</li>
          <li>use several accounts to help yourself or team up against other players;</li>
          <li>harass, threaten or spam other players;</li>
          <li>try to break into accounts or the servers, or overload them.</li>
        </ul>
        <p>Found a bug? Please tell us instead of using it.</p>
      </>
    ),
  },
  {
    title: "6. Admins and enforcement",
    body: (
      <p>
        Admins may warn players, remove names, reset coins or items, or suspend or delete accounts that break these terms. We try to be
        fair, but the final decision is ours.
      </p>
    ),
  },
  {
    title: "7. The service",
    body: (
      <p>
        The game is provided "as is". We do our best to keep it running, but it may be down, change, lose a hand in progress, or shut down.
        We are not liable for lost coins, items or game time, or for any indirect loss, as far as the law allows.
      </p>
    ),
  },
  {
    title: "8. Changes to these terms",
    body: (
      <p>
        We may update these terms. When we do, we change the date at the top of this page. If you keep playing after a change, you accept
        the new terms.
      </p>
    ),
  },
  {
    title: "9. Contact",
    body: <p>Questions about these terms? Contact the Wizard Poker team through the official Wizard Poker community channels.</p>,
  },
];

const PRIVACY: Section[] = [
  {
    title: "1. Who we are",
    body: (
      <p>
        This policy explains what Wizard Poker (wizardpoker.online) collects about you, why, and what you can do about it. We only collect
        what the game needs. We do not sell your data and we show no ads.
      </p>
    ),
  },
  {
    title: "2. What we collect",
    body: (
      <ul>
        <li>
          <strong>Account:</strong> username, display name, and your password stored only as a salted hash (we never see or store the
          password itself).
        </li>
        <li>
          <strong>Email (optional):</strong> only if you type one in, or if you log in with Discord or Google (then we save the verified
          email they give us).
        </li>
        <li>
          <strong>Discord / Google login:</strong> your account id with that service and your email. We do not get your password there.
        </li>
        <li>
          <strong>Game data:</strong> coins (including slot machine spins, shop purchases and orb pulls, which are recorded in the coin
          ledger), unlocked and equipped items, login-reward claims and free orb pulls, and what happens at the tables you play.
        </li>
        <li>
          <strong>Chat:</strong> messages you send in a table chat, with your display name. They are kept only in the table's memory (the
          last 40) so newcomers can catch up, and are gone when the table closes or goes quiet. They are not saved to the database or
          backups.
        </li>
        <li>
          <strong>Network data:</strong> your IP address is used to limit login attempts and stop abuse. For password accounts we keep
          the last few networks you logged in from, so you can still log in while someone else is guessing your password.
        </li>
      </ul>
    ),
  },
  {
    title: "3. Cookies and browser storage",
    body: (
      <ul>
        <li>One login cookie keeps you signed in (up to 30 days). It cannot be read by page scripts. We use no tracking or ad cookies.</li>
        <li>Your settings (sound, text size, effects) and the table you were at are saved in your own browser, not on our servers.</li>
      </ul>
    ),
  },
  {
    title: "4. How we use it",
    body: (
      <ul>
        <li>to run your account and the game;</li>
        <li>to keep the game fair and secure (rate limits, cheating checks);</li>
        <li>to answer you if you contact us.</li>
      </ul>
    ),
  },
  {
    title: "5. Who else sees it",
    body: (
      <ul>
        <li>Other players see your display name, your wizard's look and what you do at the table. They never see your email.</li>
        <li>Cloudflare hosts the game and stores its data for us.</li>
        <li>Discord and Google, only when you choose to log in with them.</li>
        <li>Authorities, only if the law requires it.</li>
      </ul>
    ),
  },
  {
    title: "6. How long we keep it",
    body: (
      <p>
        We keep your data while your account exists. When you delete your account, your name, email and password are erased right away and
        your coins and items are removed. Backup copies are kept for up to 7 days (daily) and 12 weeks (weekly) and then deleted.
      </p>
    ),
  },
  {
    title: "7. Your choices",
    body: (
      <ul>
        <li>Change your display name, username, password or email in Settings → Account.</li>
        <li>Delete your account in Settings → Account.</li>
        <li>Ask us for a copy of your data, or to fix it, through the contact below.</li>
      </ul>
    ),
  },
  {
    title: "8. Security",
    body: (
      <p>
        All traffic uses HTTPS. Passwords and login tokens are stored only as hashes. Coin changes are saved in all-or-nothing database
        transactions. No system is perfect, so please use a password you do not use anywhere else.
      </p>
    ),
  },
  {
    title: "9. Children",
    body: (
      <p>
        Players are expected to be 13 or older. The game is not meant for children under 13. If you think a child under 13 has an
        account, contact us and we will delete it.
      </p>
    ),
  },
  {
    title: "10. Changes and contact",
    body: (
      <p>
        If we change this policy, we update the date at the top. Questions or requests? Contact the Wizard Poker team through the official
        Wizard Poker community channels.
      </p>
    ),
  },
];

export function LegalPage({ kind }: { kind: LegalKind }) {
  const title = kind === "terms" ? "Terms of Service" : "Privacy Policy";
  const sections = kind === "terms" ? TERMS : PRIVACY;
  return (
    <main className="legal-screen">
      <RoomBackdrop />
      <article className="legal-window window" data-testid={`legal-${kind}`}>
        <header className="titlebar">
          <span className="led" aria-hidden="true" />
          <h2>Wizard Poker — {title}</h2>
        </header>
        <div className="legal-body">
          <h1>{title}</h1>
          <p className="muted small">Last updated: {LEGAL_UPDATED}</p>
          {sections.map((s) => (
            <section key={s.title}>
              <h3>{s.title}</h3>
              {s.body}
            </section>
          ))}
          <p className="legal-nav">
            <a href="/">← Back to Wizard Poker</a> ·{" "}
            <a href={kind === "terms" ? "/privacy" : "/terms"}>{kind === "terms" ? "Privacy Policy" : "Terms of Service"}</a>
          </p>
        </div>
      </article>
    </main>
  );
}
